/**
 * The browser's ear in live mode, pure so `bun test` proves it
 * (docs/plans/os-voice-conversation.md, steps 4.3 and 4.4).
 *
 * - **Earphones** (`webEarphonesOf`): whether the output the page plays to is
 *   earphones, read from `enumerateDevices()` on a `devicechange`. Chrome
 *   names the default output "Default - <device>"; a browser that names no
 *   default is read from any output it lists. Earphones in bring a call the
 *   echo detector dropped to half duplex back to full (`echoSwitchStep`,
 *   shared with the phone).
 * - **What the browser declares** (`webDuplexOf`, Phase 0's G5): desktop
 *   Chrome full when its track reads `echoCancellation` back on (0 of 25
 *   plays heard on laptop speakers); Safari, Chrome on a phone or tablet, and
 *   every browser Phase 0 did not measure, half until it is.
 * - **The frames** (`WEB_FRAME_MS`): the live call asks the microphone for
 *   50 ms frames, so the detector (`liveEarOf`, shared) sees speech within a
 *   frame and the duck lands within 150 ms; the wire keeps its 100 ms
 *   frames. The pre-roll is the newest `WEB_PRE_ROLL_MS` of those frames.
 * - **The browser's audio events** (`webAudioStep`): the track ending or no
 *   microphone left is `no_mic`; another app taking the mic (the track
 *   mutes) or the context interrupted or suspended (Safari's `interrupted`)
 *   pauses as Interrupted, which only the person's Resume (a gesture that
 *   resumes the context) brings back; earphones coming in are told once.
 */

/** How long a live call's microphone frame is: 50 ms, for the duck. */
export const WEB_FRAME_MS = 50;

/** How much of the newest frames the pre-roll keeps. */
export const WEB_PRE_ROLL_MS = 300;

/**
 * The duplex the browser declares: full on desktop Chrome with echo
 * cancellation read back on, half everywhere else. Chrome on an iPhone or
 * iPad is WebKit, and on Android it plays through a phone's speaker (G4):
 * neither is what G5 measured.
 */
export const webDuplexOf = ({ device, echoCancel }: { readonly device: string; readonly echoCancel: boolean }): 'full' | 'half' =>
  echoCancel && /^Chrome on (?:Mac|Windows|Linux|ChromeOS)$/.test(device) ? 'full' : 'half';

/** What the browser's audio is doing, as far as live mode cares. */
export type WebAudioState = {
  /** Another app or the system holds the audio: Paused, with Resume. */
  readonly interrupted: boolean;
  /** The microphone went: the call has ended for it. */
  readonly micGone: boolean;
  /** Earphones carry the page's sound. */
  readonly earphones: boolean;
};

export const NEW_WEB_AUDIO: WebAudioState = { interrupted: false, micGone: false, earphones: false };

export type WebAudioEvent =
  /** The microphone track: `onended`, `onmute`, `onunmute`. */
  | { readonly type: 'track'; readonly what: 'ended' | 'muted' | 'unmuted' }
  /** An `AudioContext`'s `onstatechange`, Safari's `interrupted` included. */
  | { readonly type: 'context'; readonly state: string }
  /** `devicechange`, read through `enumerateDevices()`: is a microphone left, and do earphones carry the sound. */
  | { readonly type: 'devices'; readonly mic: boolean; readonly earphones: boolean }
  /** The person's Resume resumed the audio. */
  | { readonly type: 'resumed' };

/** What the call does about an event: pause as Interrupted, resume, end for no microphone, or retest with earphones. */
export type WebAudioAction = 'pause' | 'resume' | 'no_mic' | 'earphones_in' | null;

export const webAudioStep = (state: WebAudioState, event: WebAudioEvent): { readonly state: WebAudioState; readonly action: WebAudioAction } => {
  const pause = (): { readonly state: WebAudioState; readonly action: WebAudioAction } =>
    state.interrupted || state.micGone ? { state, action: null } : { state: { ...state, interrupted: true }, action: 'pause' };
  const gone = (): { readonly state: WebAudioState; readonly action: WebAudioAction } =>
    state.micGone ? { state, action: null } : { state: { ...state, micGone: true }, action: 'no_mic' };
  switch (event.type) {
    case 'track':
      if (event.what === 'ended') return gone();
      // A track unmuted again offers the same Resume: nothing comes back by itself.
      return event.what === 'muted' ? pause() : { state, action: null };
    case 'context':
      return event.state === 'interrupted' || event.state === 'suspended' ? pause() : { state, action: null };
    case 'devices': {
      if (!event.mic) return gone();
      const came = event.earphones && !state.earphones;
      return { state: { ...state, earphones: event.earphones }, action: came ? 'earphones_in' : null };
    }
    case 'resumed':
      return state.interrupted && !state.micGone ? { state: { ...state, interrupted: false }, action: 'resume' } : { state, action: null };
  }
};

/** One entry of `enumerateDevices()`, as far as the ear reads it. */
export type WebMediaDevice = { readonly kind: string; readonly deviceId: string; readonly label: string };

/** Words a browser names earphones by: wired, Bluetooth, USB headsets and the common earbuds. */
const EARPHONE_WORDS = /head(phone|set)|ear(phone|bud|pod)|airpods|buds|bluetooth|hands-?free|usb audio/i;
/** And words that say the room hears it. */
const ROOM_WORDS = /speaker|display|hdmi|monitor|tv\b|built-?in/i;

const isEarphones = (label: string): boolean => EARPHONE_WORDS.test(label) && !ROOM_WORDS.test(label);

/** Whether the page plays to earphones: its default output when the browser names one, else any output it lists. */
export const webEarphonesOf = (devices: readonly WebMediaDevice[]): boolean => {
  const outputs = devices.filter((one) => one.kind === 'audiooutput' && one.label !== '');
  const chosen = outputs.find((one) => one.deviceId === 'default');
  if (chosen !== undefined) return isEarphones(chosen.label);
  return outputs.some((one) => isEarphones(one.label));
};
